<template>
  <b-container>
    <h1>Total Premium Volume</h1>
    <hr />
    <b-row>
      <b-col md="3">
        <b-form-group label="Start Date">
          <DatePicker v-model="startDate" date-id="startDate" />
        </b-form-group>
        <b-form-group label="End Date">
          <DatePicker v-model="endDate" date-id="endDate" />
        </b-form-group>
        <b-form-group label="Group By:">
          <b-form-select v-model="groupBy" :options="groupByOptions" required />
        </b-form-group>
        <b-form-group label="Business:">
          <b-form-select v-model="business" :options="businessOptions" />
        </b-form-group>
        <b-form-group label="Coverage:">
          <b-form-select v-model="coverage" :options="coverageOptions" />
        </b-form-group>
        <b-form-group label="Break Period:">
          <b-form-select v-model="breakPeriod" :options="breakOptions" />
        </b-form-group>
        <b-form-group label="Status:">
          <b-form-checkbox-group
            v-model="selectedStatus"
            :options="statusOptions"
            stacked
          />
        </b-form-group>
        <hr />
        <b-button variant="primary" @click="runReport" block :disabled="isLoading">
          Run Report
        </b-button>
      </b-col>
      <b-col>
        <div v-if="isLoading">
          <b-spinner label="Loading chart..." type="grow" small />
        </div>
        <div v-else-if="chartData">
          <line-chart :chart-data="chartData" :options="chartOptions" />
        </div>
        <div v-else>No data available.</div>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import ReportDataService from '@/services/ReportDataService';
import { Line } from 'vue-chartjs/legacy';
import DatePicker from '@/components/DatePicker.vue';

export default {
  components: {
    DatePicker,
    LineChart: {
      extends: Line,
      props: ['chartData', 'options'],
      mounted() {
        this.renderIfData();
      },
      watch: {
        chartData() {
          this.renderIfData();
        }
      },
      methods: {
        renderIfData() {
          if (this.chartData) {
            this.renderChart(this.chartData, this.options);
          }
        }
      }
    }
  },
  data() {
    const today = new Date();
    const janFirst = new Date(today.getFullYear(), 0, 1);

    return {
      results: [],
      startDate: this.formatDate(janFirst),
      endDate: this.formatDate(today),
      groupBy: 'users_name',
      business: 'none',
      coverage: 'none',
      breakPeriod: 'none',
      selectedStatus: [3, 4, 1],
      isLoading: false,
      chartData: null,
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              callback: (value) => value.toLocaleString()
            }
          },
          x: {
            type: 'category'
          }
        }
      },
      statusOptions: [
        { value: 3, text: 'Approved' },
        { value: 5, text: 'Cancelled' },
        { value: 4, text: 'Expired' },
        { value: 1, text: 'New' },
        { value: 2, text: 'Pending Underwriting' },
        { value: 7, text: 'Quoted' },
        { value: 6, text: 'Quoting' },
        { value: 8, text: 'Unbound Renewal' }
      ],
      groupByOptions: [
        { value: '', text: 'Group By' },
        { value: 'agent_name', text: 'General Agent' },
        { value: 'insurance_name', text: 'Insurance Company' },
        { value: 'policy_type_name', text: 'Risk Type' },
        { value: 'users_name', text: 'Broker' },
        { value: 'writing_user_id', text: 'Writing Broker' },
        { value: 'servicing_user_id', text: 'Servicing Broker' },
        { value: 'business_type_name', text: 'Business Type' },
        { value: 'policy_source_name', text: 'Policy Source' }
      ],
      businessOptions: [
        { value: 'none', text: 'All Business' },
        { value: 'new', text: 'New Business' },
        { value: 'renew', text: 'Renewals' }
      ],
      coverageOptions: [
        { value: 'none', text: 'All Coverage Lines' },
        { value: 'commercial', text: 'Commercial' },
        { value: 'personal', text: 'Personal' }
      ],
      breakOptions: [
        { value: 'none', text: 'Overall Total' },
        { value: 'Month', text: 'Monthly Total' },
        { value: 'Year', text: 'Yearly Total' }
      ]
    };
  },
  methods: {
    formatDate(date) {
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const dd = String(date.getDate()).padStart(2, '0');
      const yyyy = date.getFullYear();
      return `${mm}/${dd}/${yyyy}`;
    },
    runReport() {
      this.isLoading = true;
      this.chartData = null;

      const start = new Date(this.startDate).toISOString().slice(0, 10);
      const end = new Date(this.endDate).toISOString().slice(0, 10);

      ReportDataService.getTotalPremiumVolume(
        start,
        end,
        this.selectedStatus,
        this.business,
        this.coverage,
        this.groupBy,
        this.breakPeriod
      )
        .then((res) => {
          const { categories, series } = res.data;

          if (categories.length > 0) {
            // Time-series chart
            const datasets = Object.keys(series).map((key) => {
              const color = this.getRandomColor();
              return {
                label: key,
                backgroundColor: color,
                borderColor: color,
                data: series[key],
                fill: false
              };
            });

            this.chartData = {
              labels: categories,
              datasets
            };
          } else {
            // Summary chart
            const labels = Object.keys(series).filter(k => k !== 'null');
            const data = labels.map(label => series[label][0] || 0);

            this.chartData = {
              labels,
              datasets: [
                {
                  label: 'Total Premium',
                  backgroundColor: '#007bff',
                  borderColor: '#007bff',
                  data,
                  fill: false
                }
              ]
            };
          }
        })
        .catch((err) => {
          this.chartData = null;
          this.$emit('error', err);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    getRandomColor() {
      const letters = '0123456789ABCDEF';
      return '#' + Array.from({ length: 6 })
        .map(() => letters[Math.floor(Math.random() * 16)])
        .join('');
    }
  }
};
</script>
